import { CLARITY_MODEL_FAMILIES } from './constants/clarity-model-family.constant';

const VENDOR_PREFIX = /^claude-/;
const ALPHA_SEGMENT = /^[a-z]+$/;

/**
 * Extract the model family from a model id — `'claude-opus-4-6-20250514'` → `'opus'`.
 *
 * A known family wins wherever it appears in the id, so vendor-prefixed ids
 * (`'anthropic/claude-sonnet-4-5'`) resolve too. Otherwise the last purely alphabetic segment is
 * taken as the family, which keeps an unrecognised model readable rather than dropping it; an id
 * with no alphabetic segment at all is returned unchanged.
 *
 * The return type is `string`, not `ClarityModelFamily`: the fallback deliberately produces families
 * this workspace does not know, and narrowing here would be a lie. Use `isModelInFamily` to test
 * membership.
 *
 * Ported from Clarity `apps/web/src/shared/utils/modelFamilyUtils.ts`, which mirrors the backend's
 * `extractModelFamily` — the two must agree, since the backend's answer is what gets billed.
 */
export function extractModelFamily(modelId: string): string {
	const lower = modelId.toLowerCase();
	const known = CLARITY_MODEL_FAMILIES.find(family => lower.includes(family));

	if (known) {
		return known;
	}

	const segments = lower
		.replace(VENDOR_PREFIX, '')
		.split('-')
		.filter(segment => ALPHA_SEGMENT.test(segment));

	return segments.length > 0 ? segments[segments.length - 1] : modelId;
}
